/**
 * MMO Progression Manager
 * Handles ship XP, player progression, resources, and component upgrades
 */

import { EventBus } from '../utils/EventBus';

export interface ShipProgression {
  unitId: string;
  level: number;
  experience: number;
  experienceToNext: number;
  specialization: string | null;
  perks: string[];
  prestigeLevel: number;
  statBonuses: {
    hpBonus: number;
    attackBonus: number;
    defenseBonus: number;
    speedBonus: number;
  };
}

export interface PlayerProgression {
  userId: string;
  playerLevel: number;
  playerExperience: number;
  experienceToNext: number;
  unlockedHulls: string[];
  maxShipSlots: number;
  deployedShips: number;
}

export interface PlayerResources {
  userId: string;
  worldId: number;
  credits: number;
  minerals: number;
  rareMetals: number;
  techData: number;
  salvage: number;
  exoticMatter: number;
}

export interface UnitComponent {
  id: number;
  unitId: string;
  worldId: number;
  componentSlot: string;
  componentName: string;
  componentTier: number;
  bonusStats: Record<string, number>;
}

export interface UpgradeQueueEntry {
  id: number;
  userId: string;
  worldId: number;
  componentId: number;
  targetTier: number;
  ticksRemaining: number;
  startedAt: Date;
}

export class MMOProgressionManager {
  // @ts-ignore - Reserved for future use
  private _scene: Phaser.Scene;
  private shipProgression: Map<string, ShipProgression> = new Map();
  private playerProgression: PlayerProgression | null = null;
  private playerResources: PlayerResources | null = null;
  private unitComponents: Map<string, UnitComponent[]> = new Map();
  // @ts-ignore - Reserved for future use
  private _upgradeQueue: UpgradeQueueEntry[] = [];

  constructor(scene: Phaser.Scene) {
    this._scene = scene;
    this.setupMessageHandlers();
  }

  // Bound handler references for proper EventBus cleanup
  private onShipProgression = (data: ShipProgression): void => {
    this.shipProgression.set(data.unitId, data);
    EventBus.emit('ship_progression_updated', data);
  };

  private onSpecializationSet = (data: { success: boolean; unitId: string; specialization: string }): void => {
    if (data.success && this.shipProgression.has(data.unitId)) {
      const prog = this.shipProgression.get(data.unitId)!;
      prog.specialization = data.specialization;
      EventBus.emit('ship_specialization_updated', data);
    }
  };

  private onPerkAdded = (data: { success: boolean; unitId: string; perk: string }): void => {
    if (data.success && this.shipProgression.has(data.unitId)) {
      const prog = this.shipProgression.get(data.unitId)!;
      if (!prog.perks.includes(data.perk)) {
        prog.perks.push(data.perk);
      }
      EventBus.emit('ship_perk_updated', data);
    }
  };

  private onShipPrestiged = (data: { success: boolean; unitId: string }): void => {
    if (data.success) {
      this.requestShipProgression(data.unitId);
      EventBus.emit('ship_prestiged', data);
    }
  };

  private onPlayerProgression = (data: PlayerProgression): void => {
    this.playerProgression = data;
    EventBus.emit('player_progression_updated', data);
  };

  private onResources = (data: PlayerResources): void => {
    this.playerResources = data;
    EventBus.emit('player_resources_updated', data);
  };

  private onResourceTransferred = (data: { success: boolean; planetId: string; resourceType: string; amount: number }): void => {
    if (data.success) {
      this.requestPlayerResources();
      EventBus.emit('resource_transferred', data);
    }
  };

  private onComponents = (data: { unitId: string; components: UnitComponent[] }): void => {
    this.unitComponents.set(data.unitId, data.components);
    EventBus.emit('unit_components_updated', data);
  };

  private onUpgradeStarted = (data: { success: boolean; message?: string; queueId?: number }): void => {
    if (data.success) {
      this.requestPlayerResources();
      EventBus.emit('upgrade_started', data);
    } else {
      EventBus.emit('upgrade_failed', { message: data.message });
    }
  };

  private onUpgradeCancelled = (data: { success: boolean; queueId: number }): void => {
    if (data.success) {
      this.requestPlayerResources();
      EventBus.emit('upgrade_cancelled', data);
    }
  };

  private setupMessageHandlers(): void {
    EventBus.on('mmo_ship_progression', this.onShipProgression, this);
    EventBus.on('mmo_specialization_set', this.onSpecializationSet, this);
    EventBus.on('mmo_perk_added', this.onPerkAdded, this);
    EventBus.on('mmo_ship_prestiged', this.onShipPrestiged, this);
    EventBus.on('mmo_player_progression', this.onPlayerProgression, this);
    EventBus.on('mmo_resources', this.onResources, this);
    EventBus.on('mmo_resource_transferred', this.onResourceTransferred, this);
    EventBus.on('mmo_components', this.onComponents, this);
    EventBus.on('mmo_upgrade_started', this.onUpgradeStarted, this);
    EventBus.on('mmo_upgrade_cancelled', this.onUpgradeCancelled, this);
  }

  // ========================================
  // Ship Progression Methods
  // ========================================

  requestShipProgression(unitId: string, worldId: number = 1): void {
    EventBus.emit('send_message', {
      type: 'mmo_get_ship_progression',
      payload: { unitId, worldId },
    });
  }

  setSpecialization(unitId: string, specialization: string, worldId: number = 1): void {
    EventBus.emit('send_message', {
      type: 'mmo_set_specialization',
      payload: { unitId, specialization, worldId },
    });
  }

  addPerk(unitId: string, perk: string, worldId: number = 1): void {
    EventBus.emit('send_message', {
      type: 'mmo_add_perk',
      payload: { unitId, perk, worldId },
    });
  }

  prestigeShip(unitId: string, worldId: number = 1): void {
    EventBus.emit('send_message', {
      type: 'mmo_prestige_ship',
      payload: { unitId, worldId },
    });
  }

  getShipProgression(unitId: string): ShipProgression | null {
    return this.shipProgression.get(unitId) || null;
  }

  // ========================================
  // Player Progression Methods
  // ========================================

  requestPlayerProgression(worldId: number = 1): void {
    EventBus.emit('send_message', {
      type: 'mmo_get_player_progression',
      payload: { worldId },
    });
  }

  getPlayerProgression(): PlayerProgression | null {
    return this.playerProgression;
  }

  /** Always true — no ship slot limit (leveling removed). */
  canDeployShip(): boolean {
    return true;
  }

  /** Always true — all hulls available (leveling removed). */
  hasUnlockedHull(_hullType: string): boolean {
    return true;
  }

  // ========================================
  // Resource Methods
  // ========================================

  requestPlayerResources(worldId: number = 1): void {
    EventBus.emit('send_message', {
      type: 'mmo_get_resources',
      payload: { worldId },
    });
  }

  transferResources(planetId: string, resourceType: string, amount: number, worldId: number = 1): void {
    EventBus.emit('send_message', {
      type: 'mmo_transfer_resources',
      payload: { planetId, resourceType, amount, worldId },
    });
  }

  getPlayerResources(): PlayerResources | null {
    return this.playerResources;
  }

  hasResources(cost: {
    credits?: number;
    minerals?: number;
    rareMetals?: number;
    techData?: number;
    salvage?: number;
    exoticMatter?: number;
  }): boolean {
    if (!this.playerResources) return false;

    if (cost.credits && this.playerResources.credits < cost.credits) return false;
    if (cost.minerals && this.playerResources.minerals < cost.minerals) return false;
    if (cost.rareMetals && this.playerResources.rareMetals < cost.rareMetals) return false;
    if (cost.techData && this.playerResources.techData < cost.techData) return false;
    if (cost.salvage && this.playerResources.salvage < cost.salvage) return false;
    if (cost.exoticMatter && this.playerResources.exoticMatter < cost.exoticMatter) return false;

    return true;
  }

  // ========================================
  // Component Methods
  // ========================================

  requestUnitComponents(unitId: string, worldId: number = 1): void {
    EventBus.emit('send_message', {
      type: 'mmo_get_components',
      payload: { unitId, worldId },
    });
  }

  startUpgrade(componentId: number, worldId: number = 1): void {
    EventBus.emit('send_message', {
      type: 'mmo_start_upgrade',
      payload: { componentId, worldId },
    });
  }

  cancelUpgrade(queueId: number, worldId: number = 1): void {
    EventBus.emit('send_message', {
      type: 'mmo_cancel_upgrade',
      payload: { queueId, worldId },
    });
  }

  getUnitComponents(unitId: string): UnitComponent[] {
    return this.unitComponents.get(unitId) || [];
  }

  // ========================================
  // Cleanup
  // ========================================

  destroy(): void {
    EventBus.off('mmo_ship_progression', this.onShipProgression, this);
    EventBus.off('mmo_specialization_set', this.onSpecializationSet, this);
    EventBus.off('mmo_perk_added', this.onPerkAdded, this);
    EventBus.off('mmo_ship_prestiged', this.onShipPrestiged, this);
    EventBus.off('mmo_player_progression', this.onPlayerProgression, this);
    EventBus.off('mmo_resources', this.onResources, this);
    EventBus.off('mmo_resource_transferred', this.onResourceTransferred, this);
    EventBus.off('mmo_components', this.onComponents, this);
    EventBus.off('mmo_upgrade_started', this.onUpgradeStarted, this);
    EventBus.off('mmo_upgrade_cancelled', this.onUpgradeCancelled, this);

    this.shipProgression.clear();
    this.unitComponents.clear();
  }
}
